// src/components/Customer/Review.js
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Star } from "lucide-react";
import { auth, db } from "../../firebase";
import {
  doc,
  getDoc,
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  getDocs,
} from "firebase/firestore";

export default function Review() {
  const navigate = useNavigate();
  const { jobId } = useParams();

  const [job, setJob] = useState(null);
  const [tasker, setTasker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // 🔹 Load job + tasker
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        if (!jobId) return;

        const jobSnap = await getDoc(doc(db, "jobs", jobId));
        if (!jobSnap.exists()) {
          setError("Job not found.");
          return;
        }

        const jobData = { id: jobSnap.id, ...jobSnap.data() };
        if (!cancelled) setJob(jobData);

        if (jobData.taskerId) {
          const taskerSnap = await getDoc(
            doc(db, "taskers", jobData.taskerId)
          );
          if (taskerSnap.exists() && !cancelled) {
            setTasker(taskerSnap.data());
          }
        }
      } catch (e) {
        console.error(e);
        setError("Failed to load review data.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [jobId]);

  // 🔹 Validation: must be completed within 24 hours
  const reviewAllowed = useMemo(() => {
    if (!job) return false;
    if (job.status !== "completed") return false;
    if (!job.completedAt) return false;

    const completedTime = job.completedAt.toDate().getTime();
    return Date.now() - completedTime <= 24 * 60 * 60 * 1000;
  }, [job]);

  const [successMessage, setSuccessMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();

    const user = auth.currentUser;
    if (!user) return;

    // 🔒 Validate job ownership
    if (job.customerId !== user.uid) {
      setError("Unauthorized review attempt.");
      return;
    }

    if (!reviewAllowed) {
      setError("Review period expired or job not completed.");
      return;
    }

    if (rating === 0) {
      setError("Please select a rating.");
      return;
    }

    if (submitting) return;
    setSubmitting(true);

    try {
      // 🔹 Prevent duplicate review for same job
      const existingQuery = query(
        collection(db, "reviews"),
        where("jobId", "==", jobId),
        where("customerId", "==", user.uid)
      );
      const existingSnap = await getDocs(existingQuery);
      if (!existingSnap.empty) {
        setError("You already reviewed this job.");
        setSubmitting(false);
        return;
      }

      await addDoc(collection(db, "reviews"), {
        jobId,
        taskerId: job.taskerId,
        customerId: job.customerId,
        customerName: job.customerName,
        rating,
        comment: comment || "",
        createdAt: serverTimestamp(),
      });
      setSuccessMessage("Thank you for your review!");

      setTimeout(() => {
        navigate("/customer/home");
      }, 2000);
    } catch (e) {
      console.error(e);
      setError("Failed to submit review.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading...
      </div>
    );
  }

  if (error && !job) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        {error}
      </div>
    );
  }

  if (!reviewAllowed) {
    return (
      <div className="min-h-screen flex items-center justify-center text-center p-6">
        <div>
          <h2 className="text-[#1E3A8A] mb-2">Review Not Available</h2>
          <p className="text-gray-600">
            Reviews are only allowed within 24 hours after job completion.
          </p>
          <button
            onClick={() => navigate("/customer/home")}
            className="mt-6 bg-[#006C35] text-white px-6 py-3 rounded-lg"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {successMessage && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50">
          <div className="bg-[#006C35] text-white px-6 py-3 rounded-lg shadow-lg animate-fade-in">
            {successMessage}
          </div>
        </div>
      )}
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft size={24} />
          </button>
          <h2 className="text-[#1E3A8A]">Rate Your Experience</h2>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-lg p-6 shadow-sm"
        >
          {/* Tasker Info */}
          <div className="text-center mb-8">
            <img
              src={
                tasker?.imageUrl ||
                "https://via.placeholder.com/150?text=Tasker"
              }
              alt={tasker?.name || "Tasker"}
              className="w-24 h-24 rounded-full object-cover mx-auto mb-4"
            />
            <h3 className="text-[#1E3A8A] mb-1">
              {tasker?.name || "Tasker"}
            </h3>
            <p className="text-gray-600">
              {job?.subcategoryId
                ? `${job.subcategoryId} Service`
                : "Service"}
            </p>
          </div>

          {/* Rating */}
          <div className="mb-8">
            <label className="block text-center text-gray-700 mb-4">
              How was your experience?
            </label>
            <div className="flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  className="transition-transform hover:scale-110"
                >
                  <Star
                    size={48}
                    className={`${
                      star <= (hoveredRating || rating)
                        ? "text-yellow-500 fill-yellow-500"
                        : "text-gray-300"
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Comment */}
          <div className="mb-6">
            <label className="block text-gray-700 mb-2">
              Share your experience (optional)
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={5}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006C35] resize-none"
            />
          </div>

          {error && (
            <p className="text-red-600 text-sm mb-4 text-center">{error}</p>
          )}

          <button
            type="submit"
            disabled={rating === 0 || submitting}
            className="w-full bg-[#006C35] text-white py-4 rounded-lg hover:bg-[#005428] transition-colors disabled:bg-gray-300"
          >
            {submitting ? "Submitting..." : "Submit Review"}
          </button>

          <button
            type="button"
            onClick={() => navigate("/customer/home")}
            className="w-full mt-4 text-gray-600 hover:text-gray-900 py-2"
          >
            Skip for now
          </button>
        </form>
      </div>
    </div>
  );
}